import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  CardHeader,
  TextField,
  MenuItem,
  Checkbox,
  ListItemText,
  Paper,
  Chip,
  Stack,
  IconButton,
  Fade,
  Zoom,
} from '@mui/material';
import {
  AutoAwesome as AutoAwesomeIcon,
  TrendingUp as TrendingUpIcon,
  PlayArrow as PlayArrowIcon,
  Share as ShareIcon,
  AttachMoney as AttachMoneyIcon,
} from '@mui/icons-material';
import CreatorDetailsCard from './creatorDetailsCard';
import {
  getRelatedPromotions,
  platformRatesListFromCreator,
  platformRatesListFromSelectedKeys,
  promotionLabelsFromList,
  USAGE_RIGHTS_OPTIONS,
} from '../../../../Utils/constants';

const MIN_BASE_PRICE_MULTIPLIER = 0.7;

const CreatorDetails = ({
  selectedCreators,
  creatorsData,
  setCreatorsData,
  isBlitzFeeOnCreators,
  setIsBlitzFeeOnCreators,
  onNext,
  onBack,
  onRemove,
  data,
}) => {
  const [triedNext, setTriedNext] = useState(false);
  const [allPromotion, setAllPromotion] = useState('');
  const [allCrosspost, setAllCrosspost] = useState(new Set());

  useEffect(() => {
    setCreatorsData((prevData) => {
      let changed = false;
      const updated = prevData.map((creator) => {
        if (creator.originalBasePrice === undefined) {
          changed = true;
          return {
            ...creator,
            originalBasePrice: creator.basePrice || 0,
            usageRightsKey: creator.usageRights ? '30_days' : '',
            usageRightsOverride: undefined,
          };
        }
        return creator;
      });
      return changed ? updated : prevData;
    });
  }, [setCreatorsData]);

  useEffect(() => {
    let sharedRateKey = null;
    for (const creator of creatorsData) {
      if (creator.videos.length !== 1) {
        sharedRateKey = '';
        break;
      }
      const videoRateKey = creator.videos[0].rateKey;
      if (sharedRateKey === null) {
        sharedRateKey = videoRateKey;
      } else if (sharedRateKey !== videoRateKey) {
        sharedRateKey = '';
        break;
      }
    }
    setAllPromotion(sharedRateKey || '');
  }, [creatorsData]);

  const calculateBasePrice = (creator, videos = []) => {
    return videos.reduce((accum, video) => {
      const videoBase = creator[video.rateKey] || 0;
      const crossPostCost = Array.from(video.crossPostingKeys).reduce((sum, crossKey) => {
        const cost = creator[crossKey] || 0;
        return sum + Math.max(cost / 4, 0);
      }, 0);
      return accum + Math.max(videoBase, 0) + crossPostCost;
    }, 0);
  };

  const calculateUsageRightsCost = (basePrice, usageRightsKey, usageRightsOverride) => {
    if (!usageRightsKey) return 0;
    const recommended = basePrice * (USAGE_RIGHTS_OPTIONS[usageRightsKey]?.recommendedPercentage || 0);
    return usageRightsOverride !== undefined ? usageRightsOverride : recommended;
  };

  const handlePriceChange = (index, newPrice) => {
    setCreatorsData((prev) => {
      const updated = [...prev];
      const creator = { ...updated[index] };
      const numericPrice = Math.max(0, parseFloat(newPrice) || 0);
      creator.basePrice = numericPrice;
      
      if (creator.videos.length > 0 && creator.videos[0].rateKey) {
        const remainingCost = calculateBasePrice(creator, creator.videos.slice(1));
        creator[creator.videos[0].rateKey] = Math.max(0, numericPrice - remainingCost);
      }
      
      creator.adjustedPrice = numericPrice + calculateUsageRightsCost(
        numericPrice,
        creator.usageRightsKey,
        creator.usageRightsOverride
      );
      updated[index] = creator;
      return updated;
    });
  };

  const getNextId = (videos) => {
    const highestId = videos.reduce((max, video) => Math.max(max, video.id), 0);
    return highestId + 1;
  };

  const handleAddVideo = (index) => {
    setAllPromotion('');
    setAllCrosspost(new Set([]));
    setCreatorsData((prev) => {
      const updated = [...prev];
      const updatedCreator = { ...updated[index] };
      const nextId = getNextId(updatedCreator.videos);

      const updatedVideos = [
        ...updatedCreator.videos,
        {
          id: nextId,
          rateKey: '',
          crossPostingKeys: new Set([]),
        },
      ];

      updatedCreator.videos = updatedVideos;
      updated[index] = updatedCreator;
      return updated;
    });
  };

  const handleRemoveVideo = (index, videoIndex) => {
    setAllPromotion('');
    setCreatorsData((prev) => {
      const updated = [...prev];
      const updatedCreator = { ...updated[index] };
      updatedCreator.videos = updatedCreator.videos.filter((_, i) => i !== videoIndex);
      updated[index] = updatedCreator;
      return updated;
    });
  };

  const handleOptionChange = (index, option, value, videoOption = undefined, videoIndex = undefined) => {
    if (option === 'videos' && videoOption === 'rateKey') {
      setAllPromotion('');
      setAllCrosspost(new Set([]));
    }
    if (option === 'videos' && videoOption === 'crossPostingKeys') {
      setAllCrosspost(new Set([]));
    }

    setCreatorsData((prev) => {
      const updated = [...prev];
      const updatedCreator = { ...updated[index] };

      switch (option) {
        case 'videos': {
          const updatedVideos = [...updatedCreator.videos];
          const updatedVideo = { ...updatedVideos[videoIndex] };

          if (videoOption === 'crossPostingKeys') {
            updatedVideo[videoOption] = new Set([...value]);
          } else {
            if (videoOption === 'rateKey') {
              updatedVideo.crossPostingKeys = new Set();
            }
            updatedVideo[videoOption] = value;
          }
          updatedVideos[videoIndex] = updatedVideo;
          updatedCreator[option] = updatedVideos;
          break;
        }
        default:
          updatedCreator[option] = value;
      }

      updatedCreator.basePrice = calculateBasePrice(updatedCreator, updatedCreator.videos);
      updatedCreator.adjustedPrice = updatedCreator.basePrice + calculateUsageRightsCost(
        updatedCreator.basePrice,
        updatedCreator.usageRightsKey,
        updatedCreator.usageRightsOverride
      );
      updated[index] = updatedCreator;
      return updated;
    });
  };

  const handleGeneralPromotions = (event) => {
    if (event.target.value) {
      setAllPromotion(event.target.value);
      setAllCrosspost(new Set([]));
      setCreatorsData((prev) =>
        prev.map((creator) => {
          const newVideos = [
            {
              id: 1,
              rateKey: event.target.value,
              crossPostingKeys: new Set([]),
            },
          ];
          const newBasePrice = calculateBasePrice(creator, newVideos);
          return {
            ...creator,
            videos: newVideos,
            basePrice: newBasePrice,
            adjustedPrice: newBasePrice + calculateUsageRightsCost(
              newBasePrice,
              creator.usageRightsKey,
              creator.usageRightsOverride
            ),
          };
        })
      );
    } else {
      setAllPromotion('');
      setAllCrosspost(new Set([]));
      setCreatorsData((prev) =>
        prev.map((creator) => {
          return {
            ...creator,
            videos: [],
            basePrice: 0,
            adjustedPrice: 0,
          };
        })
      );
    }
  };

  const handleGeneralCrosspost = (event) => {
    const newCrossposts = new Set([...event.target.value]);
    setAllCrosspost(newCrossposts);
    setCreatorsData((prev) =>
      prev.map((creator) => {
        const newVideos = [
          {
            id: 1,
            rateKey: allPromotion,
            crossPostingKeys: newCrossposts,
          },
        ];
        const newBasePrice = calculateBasePrice(creator, newVideos);
        return {
          ...creator,
          videos: newVideos,
          basePrice: newBasePrice,
          adjustedPrice: newBasePrice + calculateUsageRightsCost(
            newBasePrice,
            creator.usageRightsKey,
            creator.usageRightsOverride
          ),
        };
      })
    );
  };

  const generalRates = useMemo(() => {
    if (creatorsData.length === 0) return [];
    const creatorRatesList = creatorsData.map(platformRatesListFromCreator);
    const commonRateKeys = creatorRatesList
      .map((rates) => rates.map((rate) => rate.rateKey))
      .reduce((commonKeys, rateKeys) => commonKeys.filter((key) => rateKeys.includes(key)));
    return creatorRatesList[0]
      .filter((rate) => commonRateKeys.includes(rate.rateKey))
      .map(({ rateKey, label }) => ({ label, rateKey }));
  }, [creatorsData]);

  const generalCrossposts = useMemo(() => {
    if (creatorsData.length === 0 || !allPromotion) return [];
    const relatedKeys = getRelatedPromotions(allPromotion);
    const creatorRatesList = creatorsData.map((c) =>
      platformRatesListFromSelectedKeys(c, relatedKeys)
    );
    const commonRateKeys = creatorRatesList
      .map((rates) => rates.map((r) => r.rateKey))
      .reduce((commonKeys, rateKeys) => commonKeys.filter((key) => rateKeys.includes(key)));
    return creatorRatesList[0]
      .filter((rate) => commonRateKeys.includes(rate.rateKey))
      .map(({ rateKey, label }) => ({ label, rateKey }));
  }, [creatorsData, allPromotion]);

  const projectSum = creatorsData.reduce((sum, c) => sum + c.adjustedPrice, 0);
  const blitzFee = isBlitzFeeOnCreators ? 0 : projectSum * 0.05;
  const finalCost = projectSum + blitzFee;

  const handleGoNext = () => {
    const invalid = creatorsData.some(
      (creator) =>
        !creator.basePrice ||
        creator.basePrice <= 0 ||
        creator.videos.length === 0
    );
    setTriedNext(true);
    if (!invalid) onNext();
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      p: { xs: 2, md: 3 }
    }}>
      {/* Animated Background Elements */}
      <Box sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflow: 'hidden',
        zIndex: 0,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '20%',
          left: '10%',
          width: '300px',
          height: '300px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
          borderRadius: '50%',
          animation: 'float 6s ease-in-out infinite'
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: '20%',
          right: '10%',
          width: '200px',
          height: '200px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)',
          borderRadius: '50%',
          animation: 'float 4s ease-in-out infinite reverse'
        },
        '@keyframes float': {
          '0%, 100%': { transform: 'translateY(0px)' },
          '50%': { transform: 'translateY(-20px)' }
        }
      }} />

      <Box sx={{ position: 'relative', zIndex: 1 }}>
        {/* Modern Header */}
        <Fade in timeout={800}>
          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <Typography 
              variant="h3" 
              sx={{ 
                color: 'white',
                fontWeight: 800,
                mb: 1,
                textShadow: '0 4px 20px rgba(0,0,0,0.3)',
                fontSize: { xs: '2rem', md: '3rem' }
              }}
            >
              Creator Details
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ 
                color: 'rgba(255,255,255,0.8)',
                fontWeight: 300,
                fontSize: { xs: '1rem', md: '1.25rem' }
              }}
            >
              Configure pricing and content for your creators
            </Typography>
          </Box>
        </Fade>

        <Grid container spacing={3}>
          <Grid item xs={12} lg={8} order={{ xs: 2, lg: 1 }}>
            <Stack spacing={3}>
              {creatorsData.map((creator, index) => (
                <Zoom in timeout={500 + index * 100} key={creator.creator}>
                  <div>
                    <CreatorDetailsCard
                      creator={creator}
                      index={index}
                      onRemove={onRemove}
                      handleOptionChange={handleOptionChange}
                      handlePriceChange={handlePriceChange}
                      handleAddVideo={handleAddVideo}
                      handleRemoveVideo={handleRemoveVideo}
                      triedNext={triedNext}
                      setCreatorsData={setCreatorsData}
                    />
                  </div>
                </Zoom>
              ))}
            </Stack>
          </Grid>

          <Grid item xs={12} lg={4} order={{ xs: 1, lg: 2 }}>
            <Fade in timeout={1000}>
              <Box sx={{ 
                position: { xs: 'static', lg: 'sticky' }, 
                top: 24,
                display: 'flex',
                flexDirection: 'column',
                gap: 3
              }}>
                {/* Quick Actions Card */}
                <Card sx={{ 
                  background: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: 4,
                  overflow: 'visible',
                  boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 30px 60px rgba(0,0,0,0.15)'
                  }
                }}>
                  <CardHeader 
                    avatar={<AutoAwesomeIcon sx={{ color: '#667eea' }} />}
                    title="Quick Setup"
                    titleTypographyProps={{ 
                      fontWeight: 700,
                      color: '#2c3e50'
                    }}
                    subheader="Apply settings to all creators"
                    subheaderTypographyProps={{
                      color: '#64748b'
                    }}
                  />
                  <CardContent sx={{ pt: 0 }}>
                    <Stack spacing={3}>
                      <TextField
                        value={allPromotion}
                        onChange={handleGeneralPromotions}
                        label="Set All Promotion Types"
                        select
                        fullWidth
                        variant="outlined"
                        InputProps={{
                          startAdornment: <PlayArrowIcon sx={{ mr: 1, color: '#667eea' }} />,
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 3,
                            backgroundColor: 'rgba(103, 126, 234, 0.04)',
                            '&:hover': {
                              backgroundColor: 'rgba(103, 126, 234, 0.08)',
                            },
                            '&.Mui-focused': {
                              backgroundColor: 'rgba(103, 126, 234, 0.08)',
                            }
                          }
                        }}
                      >
                        <MenuItem value="">
                          <em>None Selected</em>
                        </MenuItem>
                        {generalRates.map((promo) => (
                          <MenuItem key={`general-${promo.rateKey}`} value={promo.rateKey}>
                            <Chip 
                              label={promo.label} 
                              size="small" 
                              variant="outlined"
                              sx={{ mr: 1 }}
                            />
                          </MenuItem>
                        ))}
                      </TextField>

                      <TextField
                        label="Set All Crossposts"
                        select
                        fullWidth
                        variant="outlined"
                        InputProps={{
                          startAdornment: <ShareIcon sx={{ mr: 1, color: '#667eea' }} />,
                        }}
                        SelectProps={{
                          multiple: true,
                          renderValue: (selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {promotionLabelsFromList(selected).map((label) => (
                                <Chip 
                                  key={label} 
                                  label={label} 
                                  size="small"
                                  color="primary"
                                  variant="filled"
                                />
                              ))}
                            </Box>
                          ),
                        }}
                        value={[...allCrosspost]}
                        onChange={handleGeneralCrosspost}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 3,
                            backgroundColor: 'rgba(103, 126, 234, 0.04)',
                            '&:hover': {
                              backgroundColor: 'rgba(103, 126, 234, 0.08)',
                            },
                            '&.Mui-focused': {
                              backgroundColor: 'rgba(103, 126, 234, 0.08)',
                            }
                          }
                        }}
                      >
                        {generalCrossposts.map((promo) => (
                          <MenuItem key={`general-cross-${promo.rateKey}`} value={promo.rateKey}>
                            <Checkbox 
                              checked={allCrosspost.has(promo.rateKey)}
                              sx={{ color: '#667eea' }}
                            />
                            <ListItemText
                              primary={promo.label}
                              primaryTypographyProps={{ fontWeight: 500 }}
                            />
                          </MenuItem>
                        ))}
                      </TextField>
                    </Stack>
                  </CardContent>
                </Card>

                {/* Project Summary Card */}
                <Card sx={{ 
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.9) 100%)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: 4,
                  overflow: 'visible',
                  boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 30px 60px rgba(0,0,0,0.15)'
                  }
                }}>
                  <CardHeader 
                    avatar={<TrendingUpIcon sx={{ color: '#667eea' }} />}
                    title="Project Summary"
                    titleTypographyProps={{ 
                      fontWeight: 700,
                      color: '#2c3e50'
                    }}
                  />
                  <CardContent>
                    <Stack spacing={3}>
                      {/* Cost Breakdown */}
                      <Paper sx={{ 
                        p: 3, 
                        borderRadius: 3,
                        background: 'linear-gradient(135deg, #f8faff 0%, #f1f5ff 100%)',
                        border: '1px solid rgba(103, 126, 234, 0.1)'
                      }}>
                        <Stack spacing={2}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                              Creator Costs
                            </Typography>
                            <Typography variant="h6" fontWeight={600}>
                              ${projectSum.toFixed(2)}
                            </Typography>
                          </Box>
                          
                          {blitzFee > 0 && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="body2" color="text.secondary">
                                Platform Fee (5%)
                              </Typography>
                              <Typography variant="body1" fontWeight={500}>
                                ${blitzFee.toFixed(2)}
                              </Typography>
                            </Box>
                          )}
                          
                          <Box 
                            sx={{ 
                              pt: 2, 
                              borderTop: '2px solid rgba(103, 126, 234, 0.2)',
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'center' 
                            }}
                          >
                            <Typography variant="h6" fontWeight={700} color="#667eea">
                              Final Cost
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <AttachMoneyIcon sx={{ color: '#667eea', fontSize: 28 }} />
                              <Typography 
                                variant="h4" 
                                fontWeight={800} 
                                color="#667eea"
                                sx={{ textShadow: '0 2px 4px rgba(102, 126, 234, 0.2)' }}
                              >
                                {finalCost.toFixed(2)}
                              </Typography>
                            </Box>
                          </Box>
                        </Stack>
                      </Paper>

                      {/* Action Buttons */}
                      <Stack direction="row" spacing={2}>
                        <Button 
                          variant="outlined" 
                          onClick={onBack}
                          size="large"
                          fullWidth
                          sx={{ 
                            borderRadius: 3,
                            borderColor: 'rgba(103, 126, 234, 0.3)',
                            color: '#667eea',
                            fontWeight: 600,
                            py: 1.5,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              borderColor: '#667eea',
                              backgroundColor: 'rgba(103, 126, 234, 0.05)',
                              transform: 'translateY(-2px)',
                              boxShadow: '0 10px 25px rgba(103, 126, 234, 0.15)'
                            }
                          }}
                        >
                          Back
                        </Button>
                        <Button
                          variant="contained"
                          onClick={handleGoNext}
                          disabled={creatorsData.length === 0}
                          size="large"
                          fullWidth
                          sx={{ 
                            borderRadius: 3,
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            fontWeight: 700,
                            py: 1.5,
                            fontSize: '1.1rem',
                            boxShadow: '0 8px 25px rgba(102, 126, 234, 0.3)',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
                              transform: 'translateY(-3px)',
                              boxShadow: '0 15px 35px rgba(102, 126, 234, 0.4)'
                            },
                            '&:disabled': {
                              background: 'linear-gradient(135deg, #cbd5e0 0%, #a0aec0 100%)'
                            }
                          }}
                        >
                          Continue
                        </Button>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              </Box>
            </Fade>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default CreatorDetails;